// components/entities/grids/PropertiesGrid.tsx
'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { createScopedOrgMemberToolbarExtras } from '@/components/entities/tables/ScopedOrgMemberToolbarExtras';
import { useScopedOrgMemberFilters } from '@/components/entities/tables/useScopedOrgMemberFilters';
import { EntityView } from '../EntityView';
import { Grid } from '@/components/portal/Grid';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { Type, MapPin, Users } from '@/components/ui/lucide-memo';
import type { LeasingPropertyFilters, PropertySortField } from '#types/types';

const translations = {
  en: {
    allAssets: 'All assets',
    breadcrumb: 'Inventory / Assets',
    newAsset: 'New asset',
    searchPlaceholder: 'Search assets...',
    sortOptions: {
      name: 'Alphabetical',
      city: 'City',
      unitCount: 'Unit Count',
      owner: 'Owner',
      occupancy: 'Occupancy Rate',
    },
    filterOptions: {
      asset: 'Asset',
      country: 'Country',
      assetType: 'Asset type',
      owner: 'Owner',
    },
  },
  fr: {
    allAssets: 'Tous les actifs',
    breadcrumb: 'Inventaire / Actifs',
    newAsset: 'Nouvel actif',
    searchPlaceholder: 'Rechercher des actifs...',
    sortOptions: {
      name: 'Alphabetique',
      city: 'Ville',
      unitCount: 'Nb unites',
      owner: 'Proprietaire',
      occupancy: 'Taux occupation',
    },
    filterOptions: {
      asset: 'Actif',
      country: 'Pays',
      assetType: "Type d'actif",
      owner: 'Proprietaire',
    },
  },
};

// The card owns the click handler so the memoized PropertyRenderer under EntityView keeps a stable
// onClick reference across grid re-renders, instead of one new closure per card per render.
const PropertyGridCard = memo(({ propertyId, onOpen }: { propertyId: string; onOpen: (propertyId: string) => void }) => {
  const handleClick = useCallback(() => onOpen(propertyId), [propertyId, onOpen]);
  return <EntityView type="Property" entityId={propertyId} variant="card" onClick={handleClick} />;
});
PropertyGridCard.displayName = 'PropertyGridCard';

interface PropertiesGridProps {
  initialFilters?: Partial<LeasingPropertyFilters>;
  lockedFilters?: Partial<LeasingPropertyFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  showDefaultToolbarSelectors?: boolean;
  renderCustomToolbarHeader?: () => React.ReactNode;
  onCreateClick?: () => void;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
}

const PropertiesGrid = memo(({
  initialFilters,
  lockedFilters,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  showDefaultToolbarSelectors = true,
  renderCustomToolbarHeader,
  onCreateClick,
  viewModeVisual,
  onViewModeVisualChange,
}: PropertiesGridProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const currentOrg = useKernelStore((state) => {
    const organisationId = state.userContext.organisationId;
    if (!organisationId) return null;
    return state.inventory.Organisation.registry[organisationId] ?? null;
  });
  const t = translations[lang];
  const shouldSkipDefaultOrganisationFilter =
    userContext.isFaterAdmin ||
    userContext.isFaterOrgMember ||
    currentOrg?.name?.trim().toLowerCase() === 'fater';

  const [gridFilters, setGridFilters] = useState<Partial<LeasingPropertyFilters>>(() => ({
    ...(initialFilters || {}),
    ...(lockedFilters || {}),
    owner: lockedFilters?.owner ?? initialFilters?.owner,
    managerId: lockedFilters?.managerId ?? initialFilters?.managerId,
    sortBy: lockedFilters?.sortBy ?? initialFilters?.sortBy ?? 'UNIT_COUNT',
    sortDir: lockedFilters?.sortDir ?? initialFilters?.sortDir ?? 'desc',
    organisationId:
      lockedFilters?.organisationId ??
      initialFilters?.organisationId ??
      (shouldSkipDefaultOrganisationFilter ? undefined : currentOrg?.id ?? undefined),
  }));
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('grid');
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;
  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);

  const {
    selectedOrgId,
    activeOrgId,
    selectedUser,
    hasDefaultToolbarSelectors,
    showOrganisationSelector,
    showUserSelector,
    handleOrgSelect,
    handleUserSelect,
  } = useScopedOrgMemberFilters<LeasingPropertyFilters>({
    tableFilters: gridFilters,
    setTableFilters: setGridFilters,
    lockedFilters,
    userFilterKey: 'managerId',
    showDefaultToolbarSelectors,
  });

  const sortOptions: { label: string; field: PropertySortField }[] = useMemo(() => [
    { label: t.sortOptions.name, field: 'NAME' },
    { label: t.sortOptions.city, field: 'CITY' },
    { label: t.sortOptions.unitCount, field: 'UNIT_COUNT' },
    { label: t.sortOptions.owner, field: 'OWNER' },
    { label: t.sortOptions.occupancy, field: 'OCCUPANCY' },
  ], [t]);
  const filterOptions = useMemo(
    () => [
      { key: 'name', label: t.filterOptions.asset, icon: <Type size={16} strokeWidth={1.9} /> },
      { key: 'country', label: t.filterOptions.country, icon: <MapPin size={16} strokeWidth={1.9} /> },
      { key: 'assetType', label: t.filterOptions.assetType, icon: <Type size={16} strokeWidth={1.9} /> },
      { key: 'owner', label: t.filterOptions.owner, icon: <Users size={16} strokeWidth={1.9} /> },
    ],
    [t]
  );

  // An asset has no page of its own any more. What you actually want when you open one is its
  // sourcing surface, so the card goes there: same focus, with the action type that routes
  // ChatContextDashboard to SourcingCenterView instead of to an entity page.
  const handleOpenProperty = useCallback((propertyId: string) => {
    if (!propertyId) return;
    void kernel.current?.focusEntityUI('Property', propertyId, 'ORCHESTRATE_SOURCING');
  }, [kernel]);

  const canShowViewModeToggle =
    hasDefaultToolbarSelectors && supportsInventoryViewModeToggle('Property');
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const renderPropertyCard = useCallback(
    (property: { id: string }) => (
      <PropertyGridCard key={property.id} propertyId={property.id} onOpen={handleOpenProperty} />
    ),
    [handleOpenProperty]
  );

  return (
    <Grid
      entityType="Property"
      pageSize={24}
      initialFilters={gridFilters}
      lockedFilters={lockedFilters}
      sortOptions={sortOptions}
      filterOptions={filterOptions}
      assetLedgerTitle={assetLedgerTitle || t.allAssets}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb || t.breadcrumb}
      searchPlaceholder={t.searchPlaceholder}
      onCreateClick={onCreateClick}
      createButtonLabel={onCreateClick ? t.newAsset : undefined}
      renderToolbarExtras={createScopedOrgMemberToolbarExtras({
        renderCustomToolbarHeader: effectiveToolbarHeader,
        hasDefaultToolbarSelectors,
        showOrganisationSelector,
        showUserSelector,
        selectedOrgId,
        selectedUser,
        activeOrgId: activeOrgId ?? undefined,
        onOrgSelect: handleOrgSelect,
        onUserSelect: handleUserSelect,
      })}
      renderCard={renderPropertyCard}
    />
  );
});

PropertiesGrid.displayName = 'PropertiesGrid';
export { PropertiesGrid };
